














import FullCalendar from '@fullcalendar/vue';
import {
  CalendarApi,
  CalendarOptions,
  DateSelectArg,
  DatesSetArg,
  DayHeaderContentArg,
  EventClickArg,
  EventDropArg,
  ViewApi,
  WeekNumberContentArg,
} from '@fullcalendar/core';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentPlugin from '@fullcalendar/moment';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { weekdayEnum } from '../../../shared/enums/weekdayEnum';
import { ResourceApi } from '@fullcalendar/resource';
import { userTypes } from '../../../shared/enums/userTypes.enum';
import { CalendarViewType } from '../../../shared/enums/calendarViewType';
import ContactAvatar from '../contacts/ContactAvatar.vue';
import CalendarViewTimelineViewEvent from './CalendarViewTimelineViewEvent.vue';
import CalendarViewNoticeBoardEvent from './CalendarViewNoticeBoardEvent.vue';
import CalendarViewMonthViewEvent from './CalendarViewMonthViewEvent.vue';
import CalendarViewListViewEvent from './CalendarViewListViewEvent.vue';

const SINGLE_DAY_MAX_EVENTS = 3;
const MULTI_DAY_MAX_EVENTS = 2;

export default {
  components: {
    CalendarViewListViewEvent,
    CalendarViewMonthViewEvent,
    CalendarViewNoticeBoardEvent,
    ContactAvatar,
    CalendarViewTimelineViewEvent,
    FullCalendar,
  },
  props: {
    eventSources: { type: Array, required: true },
    resources: { type: Function, default: null },
    initialView: { type: String, default: CalendarViewType.FIVE_DAYS_GRID },
  },
  emits: ['eventClick', 'dateSelect', 'loading', 'eventDrop', 'datesSet'],
  computed: {
    options(): CalendarOptions {
      return {
        schedulerLicenseKey: process.env.VUE_APP_FULLCALENDAR_LICENSE_KEY,
        locale: 'da',
        plugins: [resourceTimeGridPlugin, listPlugin, dayGridPlugin, interactionPlugin, momentPlugin],
        dayHeaderFormat: { weekday: 'long', day: 'numeric', month: 'long' },
        titleFormat: { month: 'long', day: '2-digit', year: 'numeric' },
        customButtons: {
          today: {
            text: this.$t('calendar.today'),
            click: this.gotoTodayView,
          },
        },
        buttonText: {
          [CalendarViewType.DAY_GRID]: this.$t('calendar.day'),
          [CalendarViewType.WEEK_GRID]: this.$t('calendar.week'),
          month: this.$t('calendar.month'),
          [CalendarViewType.FIVE_DAYS_GRID]: this.$t('calendar.five_days'),
        },
        headerToolbar: {
          start: 'prev,next title',
          end: `${CalendarViewType.DAY_GRID} ${CalendarViewType.FIVE_DAYS_GRID} ${CalendarViewType.WEEK_GRID} ${CalendarViewType.DAY_GRID_MONTH} today`,
        },
        views: {
          [CalendarViewType.DAY_GRID_MONTH]: {
            dayHeaderFormat: { weekday: 'long', week: 'long' },
            titleFormat: { year: 'numeric' },
            weekNumbers: true,
            weekNumberContent: this.getWeekNumberContent,
          },
          [CalendarViewType.FIVE_DAYS_GRID]: {
            type: 'resourceTimeGridWeek',
            weekends: false,
            weekNumbers: false,
            dayMaxEvents: MULTI_DAY_MAX_EVENTS,
            allDayText: '',
            slotLabelFormat: { hour: '2-digit', minute: '2-digit' },
            eventTimeFormat: '',
          },
          [CalendarViewType.EVENTS_LIST]: {
            type: 'listDay',
            dayMaxEvents: SINGLE_DAY_MAX_EVENTS,
            duration: { days: 30 },
            dayHeaderContent: this.getListViewHeaderContent,
            allDayText: this.$t('calendar.all_day_short'),
          },
          [CalendarViewType.DAY_GRID]: {
            slotLabelFormat: { hour: '2-digit', minute: '2-digit' },
            titleFormat: {
              month: 'long',
              day: '2-digit',
              year: 'numeric',
              weekday: 'long',
            },
            weekNumbers: false,
            dayMaxEvents: SINGLE_DAY_MAX_EVENTS,
            allDayText: '',
          },
          [CalendarViewType.WEEK_GRID]: {
            type: 'resourceTimeGridWeek',
            weekNumbers: false,
            dayMaxEvents: MULTI_DAY_MAX_EVENTS,
            allDayText: '',
            slotLabelFormat: { hour: '2-digit', minute: '2-digit' },
            eventTimeFormat: '',
          },
          [CalendarViewType.NOTICE_BOARD]: {
            type: 'listDay',
            dayMaxEvents: SINGLE_DAY_MAX_EVENTS,
            duration: { days: 30 },
            allDayText: this.$t('calendar.all_day_short'),
            dayHeaderContent: this.getListViewHeaderContent,
          },
        },
        contentHeight: 700,
        initialView: this.initialView,
        initialDate: DateTimeUtil.getStartOf(new Date(), 'week'),
        firstDay: weekdayEnum.MONDAY,
        scrollTime: { hour: 8, minutes: 0 },
        nowIndicator: true,
        nowIndicatorContent: '',
        moreLinkText: this.$t('button.more'),
        dayPopoverFormat: { weekday: 'long', day: 'numeric', month: 'long' },
        datesAboveResources: true,
        lazyFetching: false,
        selectable: true,
        noEventsText: this.$t('calendar.no_events'),
        resources: this.resources,
        eventSources: this.eventSources,
        eventClick: this.emitEventClick,
        select: this.emitDateSelect,
        loading: this.emitLoading,
        eventDrop: this.emitEventDrop,
        datesSet: this.emitDatesSet,
        viewDidMount: this.handleViewMount,
      };
    },
  },
  mounted() {
    this.addPrintEvents();
  },
  beforeDestroy() {
    this.removePrintEvents();
  },
  methods: {
    getEventViewComponent(view: ViewApi) {
      switch (view.type) {
        case CalendarViewType.DAY_GRID_MONTH:
          return CalendarViewMonthViewEvent;
        case CalendarViewType.NOTICE_BOARD:
          return CalendarViewNoticeBoardEvent;
        case CalendarViewType.EVENTS_LIST:
          return CalendarViewListViewEvent;
        default:
          return CalendarViewTimelineViewEvent;
      }
    },
    emitEventClick(eventInfo: EventClickArg) {
      if (eventInfo.view.type === CalendarViewType.NOTICE_BOARD) {
        return;
      }
      this.$emit('eventClick', eventInfo);
    },
    emitDateSelect(dateInfo: DateSelectArg) {
      this.$emit('dateSelect', dateInfo);
    },
    emitLoading(isLoading: boolean) {
      this.$emit('loading', isLoading);
    },
    emitEventDrop(eventInfo: EventDropArg) {
      this.$emit('eventDrop', eventInfo);
    },
    emitDatesSet(dateInfo: DatesSetArg) {
      this.$emit('datesSet', dateInfo);
    },
    getApi(): CalendarApi {
      return this.$refs.calendar.getApi();
    },
    getListViewHeaderContent(arg: DayHeaderContentArg) {
      return DateTimeUtil.formatDate(arg.date, 'dddd [den] DD. MMM YYYY');
    },
    getWeekNumberContent(arg: WeekNumberContentArg) {
      return `Uge ${DateTimeUtil.formatDate(arg.date, 'WW')}`;
    },
    getAvatarProfileFromResource(resource: ResourceApi) {
      return { ...resource.extendedProps, id: parseInt(resource.extendedProps.singleId) };
    },
    getIsResourceProfileDisabled(resource: ResourceApi) {
      return resource.extendedProps.type !== userTypes.PROFILE;
    },
    gotoTodayView() {
      const calendar: CalendarApi = this.getApi();
      switch (calendar.view.type) {
        case CalendarViewType.WEEK_GRID:
        case CalendarViewType.FIVE_DAYS_GRID:
          calendar.gotoDate(DateTimeUtil.getStartOf(new Date(), 'week'));
          break;
        default:
          calendar.gotoDate(new Date());
      }
    },
    handleViewMount() {
      this.getApi().setOption('contentHeight', this.getDefaultContentHeight());
    },
    getDefaultContentHeight(): string | number {
      const calendar: CalendarApi = this.getApi();
      switch (calendar.view.type) {
        case CalendarViewType.NOTICE_BOARD:
        case CalendarViewType.EVENTS_LIST:
          return 'auto';
        default:
          return 700;
      }
    },
    getDefaultDayMaxEvents(): number | boolean {
      const calendar: CalendarApi = this.getApi();
      switch (calendar.view.type) {
        case CalendarViewType.WEEK_GRID:
        case CalendarViewType.FIVE_DAYS_GRID:
          return MULTI_DAY_MAX_EVENTS;
        case CalendarViewType.NOTICE_BOARD:
        case CalendarViewType.DAY_GRID:
        case CalendarViewType.EVENTS_LIST:
          return SINGLE_DAY_MAX_EVENTS;
        default:
          return false;
      }
    },
    addPrintEvents() {
      window.addEventListener('beforeprint', this.expandCalendar);
      window.addEventListener('afterprint', this.collapseCalendar);
    },
    removePrintEvents() {
      window.removeEventListener('beforeprint', this.expandCalendar);
      window.removeEventListener('afterprint', this.collapseCalendar);
    },
    expandCalendar() {
      this.getApi().setOption('contentHeight', 'auto');
      this.getApi().setOption('dayMaxEvents', false);
    },
    collapseCalendar() {
      this.getApi().setOption('dayMaxEvents', this.getDefaultDayMaxEvents());
      this.getApi().setOption('contentHeight', this.getDefaultContentHeight());
    },
  },
};
